import { useForm } from '@tanstack/react-form';
import { Button, Card, Input } from '@ui/themed';
import { t } from 'i18next';
import { useHandlers, useSite } from '../../../../context/page-context';
import { useUpdateAccount } from '../hooks/use-update-account';
import {
  type AccountSettingsUpdateInformationFormValues,
  updateAccountFormSchema,
} from '../schemas';

export function PersonalInfoForm() {
  const updateAccountDetails = useUpdateAccount();
  const { userName, userEmail } = useSite();
  const { notificationHandler, authHandler } = useHandlers();

  const defaultValues: AccountSettingsUpdateInformationFormValues = {
    name: userName ?? '',
    email: userEmail ?? '',
  };

  const form = useForm({
    defaultValues,
    validators: { onSubmit: updateAccountFormSchema },
    onSubmit: ({ value }) => {
      const payload = updateAccountFormSchema.parse(value);

      updateAccountDetails.mutate(payload, {
        onError: () => {
          notificationHandler.error();
        },
        onSuccess: () => {
          authHandler.refreshUser();
          notificationHandler.success(t('notifications:saved'));
        },
      });
    },
  });

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        e.stopPropagation();
        await form.handleSubmit();
      }}
    >
      <Card>
        <Card.Header headingLevel="h2">
          {t('features:account.pages.settings_account_page.personal_info.header')}
        </Card.Header>
        <Card.Body>
          <div className="grid grid-cols-2 gap-4">
            <form.Field name="name">
              {(field) => (
                <Input
                  label={t(
                    'features:account.pages.settings_account_page.personal_info.labels.name',
                  )}
                  value={field.state.value}
                  onChange={field.handleChange}
                  error={field.state.meta.errors[0]?.message}
                />
              )}
            </form.Field>
            <form.Field name="email">
              {(field) => (
                <Input
                  label={t(
                    'features:account.pages.settings_account_page.personal_info.labels.email',
                  )}
                  value={field.state.value}
                  onChange={field.handleChange}
                  error={field.state.meta.errors[0]?.message}
                />
              )}
            </form.Field>
          </div>
          <form.Subscribe selector={(state) => state.isSubmitting}>
            {(isSubmitting) => (
              <Button type="submit" disabled={isSubmitting || updateAccountDetails.isPending}>
                {t('buttons:save')}
              </Button>
            )}
          </form.Subscribe>
        </Card.Body>
      </Card>
    </form>
  );
}
